import Bugsnag from "@bugsnag/js";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import Link from "@Components/atoms/Link";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconArrow } from "@Icons/16x16/arrow-right.svg";
import { ReactComponent as IconLock } from "@Icons/16x16/lock.svg";
import { ReactComponent as IconSearch } from "@Icons/search.svg";
import cheerzLogo from "@Images/logo-cheerz.png";
import { Partnership as PartnershipContent } from "@Types/partnership";
import { homeFaqLink } from "@Utils/links";
import { setMeta } from "@Utils/meta";
import { setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  data: PartnershipContent;
  onDisplayLoginModal: () => void;
}

const Partnership: FunctionComponent<Props> = ({ data, onDisplayLoginModal }) => {
  const { isSmallScreen } = useBreakpoint();
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  const [displayValidationButton, setDisplayValidationButton] = useState<boolean>(true);
  const intl = useIntl();
  const promoInputRef = useRef<HTMLInputElement>(null);

  const handleClickDiscoverButton = () => {
    if (!data.userLogged && data.registeredOnly) {
      onDisplayLoginModal();
    } else {
      setDisplayValidationButton(false);
    }
    setDataLayerTrack("Partner Page Discover Clicked", {
      category: "Partnership",
      message: data.tag,
      position: "partnership",
    });
    setCopiedCode(false);
  };

  const handleClickOfferButton = () => {
    setDataLayerTrack("Promotion Clicked", {
      category: "Partnership",
      message: data.tag,
      position: "partnership",
    });
    window.location.href = `${data.ctaLink}`;
  };

  const handleCopyButton = () => {
    if (!!data.codeValue) {
      // New API supported since 2018/2020/2021 depending on the OS
      if (!!navigator.clipboard) {
        navigator.clipboard
          .writeText(data.codeValue)
          .then(() => setCopiedCode(true))
          .catch((error: Error) => Bugsnag.notify(error));
      } else if (!!promoInputRef.current) {
        promoInputRef.current.select();
        // Deprecated API but supported by all OS
        document.execCommand("copy");
        setCopiedCode(true);
      }
    }
  };

  useEffect(() => {
    document.title = data.title;
    if (!data.referenced) {
      setMeta("robots", "noindex");
    }
    setDataLayerTrack("Promotion Viewed", {
      category: "Partnership",
      message: data.tag,
      position: "partnership",
    });
  }, []);

  return (
    <div className="Partnership">
      <div
        className="Partnership-image"
        style={{
          backgroundImage: `url(${isSmallScreen ? data.mainImageReducedUrl : data.mainImageUrl})`,
        }}
      />

      <div className="Partnership-content">
        <div className="Partnership-content-main">
          <h1 className="Partnership-content-title">{data.title}</h1>
          {data.cheerzBrandingText && (
            <div className="Partnership-content-cheerzBranding">
              <div className="Partnership-content-cheerzBranding-image">
                <img
                  alt="Cheerz"
                  className="Partnership-content-cheerzBranding-logo"
                  src={cheerzLogo}
                />
              </div>
              <div className="Partnership-content-cheerzBranding-text">
                <p dangerouslySetInnerHTML={{ __html: data.cheerzBrandingText }}></p>
              </div>
            </div>
          )}

          <div className="Partnership-content-partner">
            {data.partnerLogoUrl && (
              <div className="Partnership-content-partner-image">
                <img className="Partnership-content-partner-logo" src={data.partnerLogoUrl} />
              </div>
            )}
            <div className="Partnership-content-partner-text">
              <p dangerouslySetInnerHTML={{ __html: data.partnershipText }}></p>
            </div>
          </div>

          {data.campaignType !== "noCode" ? (
            <div className="Partnership-content-informations">
              {displayValidationButton && (
                <ButtonSmily
                  icon={<IconLock />}
                  iconPosition="left"
                  onClick={handleClickDiscoverButton}
                  size="m"
                  type="primary"
                >
                  {intl.formatMessage({ id: "cheerz_Partnership_button" })}
                </ButtonSmily>
              )}
              <div
                className="Partnership-content-informations-discover"
                style={
                  displayValidationButton
                    ? { height: 0, opacity: 0 }
                    : { minHeight: "175px", opacity: 1 }
                }
              >
                <div className="Partnership-content-informations-discover-code">
                  <div className="Partnership-content-informations-discover-code-buttons">
                    <ButtonSmily onClick={handleCopyButton} size="s" type="secondary">
                      {intl.formatMessage(
                        { id: "cheerz_Partnership_code_button" },
                        { code: data.codeValue }
                      )}
                    </ButtonSmily>
                    <ButtonSmily onClick={handleCopyButton} size="s" type="primary">
                      {intl.formatMessage({ id: "cheerz_Partnership_copy_button" })}
                    </ButtonSmily>
                  </div>
                  {copiedCode ? (
                    <p className="Partnership-content-informations-discover-text copied">
                      {intl.formatMessage({ id: "cheerz_Partnership_copied" })}
                    </p>
                  ) : (
                    <p className="Partnership-content-informations-discover-text">
                      {intl.formatMessage({ id: "cheerz_Partnership_copy_instructions" })}
                    </p>
                  )}
                  {!!data.codeValue && (
                    <input
                      readOnly
                      ref={promoInputRef}
                      style={{ visibility: "hidden" }}
                      type="text"
                      value={data.codeValue}
                    />
                  )}
                </div>
                {!!data.instructionsText && (
                  <div className="Partnership-content-informations-discover-instructions">
                    <IconSearch />
                    <h3 className="Partnership-content-informations-discover-instructions-title">
                      {intl.formatMessage({ id: "cheerz_Partnership_instructions" })}
                    </h3>
                    <p className="Partnership-content-informations-discover-instructions-text">
                      {data.instructionsText}
                    </p>
                  </div>
                )}

                <ButtonSmily
                  icon={<IconArrow />}
                  iconPosition="right"
                  onClick={handleClickOfferButton}
                  size="m"
                  type="primary"
                >
                  {data.offerButtonText}
                </ButtonSmily>
              </div>
            </div>
          ) : (
            <div className="Partnership-content-informations">
              <ButtonSmily onClick={handleClickOfferButton} size="m" type="primary">
                {data.offerButtonText}
              </ButtonSmily>
            </div>
          )}
        </div>

        <div className="Partnership-content-mentions">
          <Link href={homeFaqLink[intl.locale]} icon={<IconArrow />} iconPosition="right" size="s">
            {intl.formatMessage({ id: "cheerz_Partnership_help" })}
          </Link>
          <p className="Partnership-content-mentions-title">
            {intl.formatMessage({ id: "cheerz_Partnership_mentions" })}
          </p>
          {data.codeExpirationDate && (
            <p className="Partnership-content-mentions-terms">
              {intl.formatMessage(
                { id: "cheerz_Partnership_availability" },
                { date: new Date(data.codeExpirationDate).toLocaleDateString() }
              )}
            </p>
          )}
          <p
            className="Partnership-content-mentions-terms"
            dangerouslySetInnerHTML={{ __html: data.partnershipConditions }}
          ></p>
        </div>
      </div>
    </div>
  );
};

export default Partnership;
